import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/auth'
import {
  Button,
  PageLoading,
  PageHeader,
  FilterBar,
  SearchFilter,
  SelectFilter,
  Table,
  Badge,
  Card,
  SummaryFooter,
  NoOrganizationMessage,
  formatEuro,
  Input,
  Select,
  FormField,
  FormActions,
} from '../components'
import { transactionService } from '../api/services/TransactionService'
import type { Transaction } from '../api/types'

interface TransactionFormData {
  date: string
  amount: string
  description: string
  counterparty: string
  category: string
  accountCode: string
  vatRate: string
}

export default function TransactionsPage() {
  const { t } = useTranslation()
  const { activeOrg } = useAuthStore()
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('')
  const [periodFilter, setPeriodFilter] = useState('')
  
  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [formSubmitting, setFormSubmitting] = useState(false)
  
  // Form state
  const [formData, setFormData] = useState<TransactionFormData>({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
    counterparty: '',
    category: '',
    accountCode: '',
    vatRate: '',
  })

  // Category options with translations
  const CATEGORY_OPTIONS = useMemo(() => [
    { value: '', label: t('transactions.categories.uncategorized') },
    { value: 'office', label: t('transactions.categories.office') },
    { value: 'travel', label: t('transactions.categories.travel') },
    { value: 'meals', label: t('transactions.categories.meals') },
    { value: 'software', label: t('transactions.categories.software') },
    { value: 'hardware', label: t('transactions.categories.hardware') },
    { value: 'marketing', label: t('transactions.categories.marketing') },
    { value: 'rent', label: t('transactions.categories.rent') },
    { value: 'utilities', label: t('transactions.categories.utilities') },
    { value: 'salary', label: t('transactions.categories.salary') },
    { value: 'consulting', label: t('transactions.categories.consulting') },
    { value: 'sales', label: t('transactions.categories.sales') },
    { value: 'other', label: t('transactions.categories.other') },
  ], [t])

  // Account code options with translations
  const ACCOUNT_CODE_OPTIONS = useMemo(() => [
    { value: '', label: t('transactions.accounts.notSelected') },
    { value: '1000', label: t('transactions.accounts.cash') },
    { value: '1700', label: t('transactions.accounts.receivables') },
    { value: '2000', label: t('transactions.accounts.payables') },
    { value: '3000', label: t('transactions.accounts.sales') },
    { value: '4000', label: t('transactions.accounts.purchases') },
    { value: '5000', label: t('transactions.accounts.services') },
    { value: '6000', label: t('transactions.accounts.premises') },
    { value: '7000', label: t('transactions.accounts.personnel') },
    { value: '7600', label: t('transactions.accounts.travel') },
    { value: '7800', label: t('transactions.accounts.marketing') },
  ], [t])

  // Load transactions
  useEffect(() => {
    if (!activeOrg) return
    loadTransactions()
  }, [activeOrg])

  const loadTransactions = async () => {
    if (!activeOrg) return
    setLoading(true)
    try {
      const response = await transactionService.list({
        organizationId: activeOrg.id,
        limit: 100,
      })
      setTransactions(response.transactions || [])
      setError('')
    } catch {
      setError(t('transactions.errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    let result = transactions.filter((tx) =>
      filter === '' ||
      tx.description?.toLowerCase().includes(filter.toLowerCase()) ||
      tx.counterparty?.toLowerCase().includes(filter.toLowerCase()) ||
      tx.category?.toLowerCase().includes(filter.toLowerCase()) ||
      (tx.accountCode || '').includes(filter)
    )
    
    if (periodFilter) {
      result = result.filter(tx => tx.date.startsWith(periodFilter))
    }
    
    return result
  }, [transactions, filter, periodFilter])

  const totalAmount = useMemo(() => {
    return filteredTransactions.reduce((sum, tx) => sum + (tx.amount || 0), 0)
  }, [filteredTransactions])

  // Generate period options from data
  const periodOptions = useMemo(() => {
    const years = new Set<string>()
    transactions.forEach(tx => {
      if (tx.date) {
        years.add(tx.date.substring(0, 4))
      }
    })
    return [
      { value: '', label: t('transactions.allYears') },
      ...Array.from(years).sort().reverse().map(year => ({
        value: year,
        label: year,
      }))
    ]
  }, [transactions, t])

  // Form handlers
  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      amount: '',
      description: '',
      counterparty: '',
      category: '',
      accountCode: '',
      vatRate: '',
    })
    setEditingTransaction(null)
  }

  const openCreateModal = () => {
    resetForm()
    setShowModal(true)
  }

  const openEditModal = (tx: Transaction) => {
    setEditingTransaction(tx)
    setFormData({
      date: tx.date,
      amount: tx.amount.toString(),
      description: tx.description || '',
      counterparty: tx.counterparty || '',
      category: tx.category || '',
      accountCode: tx.accountCode || '',
      vatRate: '',
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeOrg) return
    
    setFormSubmitting(true)
    try {
      const amount = parseFloat(formData.amount.replace(',', '.'))
      if (isNaN(amount)) {
        throw new Error(t('transactions.errors.invalidAmount'))
      }

      const payload = {
        organizationId: activeOrg.id,
        date: formData.date,
        amount,
        description: formData.description || undefined,
        counterparty: formData.counterparty || undefined,
        category: formData.category || undefined,
        accountCode: formData.accountCode || undefined,
        vatRate: formData.vatRate ? parseFloat(formData.vatRate) : undefined,
      }

      if (editingTransaction) {
        await transactionService.update(editingTransaction.id, payload)
      } else {
        await transactionService.create(payload)
      }

      setShowModal(false)
      resetForm()
      await loadTransactions()
    } catch (err: any) {
      setError(err.message || t('transactions.errors.saveFailed'))
    } finally {
      setFormSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!activeOrg) return
    
    try {
      await transactionService.delete(id)
      setShowDeleteConfirm(null)
      await loadTransactions()
    } catch (err: any) {
      setError(err.message || t('transactions.errors.deleteFailed'))
    }
  }

  if (!activeOrg) {
    return <NoOrganizationMessage />
  }

  if (loading && transactions.length === 0) {
    return <PageLoading message={t('transactions.errors.loading')} />
  }

  const columns = [
    {
      key: 'date',
      header: `${t('transactions.date')} / ${t('transactions.voucherNumber')}`,
      render: (tx: Transaction) => (
        <div>
          <div className="text-sm text-gray-900">{tx.date}</div>
          <div className="text-xs text-gray-400">#{tx.voucherNumber ?? '-'}</div>
        </div>
      ),
    },
    {
      key: 'description',
      header: t('transactions.description'),
      render: (tx: Transaction) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {tx.description || t('transactions.noDescription')}
          </div>
          {tx.counterparty && (
            <div className="text-xs text-gray-500">{tx.counterparty}</div>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: t('transactions.category'),
      render: (tx: Transaction) => (
        <div>
          <Badge variant="neutral">{tx.category || t('transactions.noCategory')}</Badge>
          {tx.accountCode && (
            <div className="text-xs text-gray-400 mt-1">{t('transactions.accountCode')} {tx.accountCode}</div>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('transactions.amount'),
      align: 'right' as const,
      render: (tx: Transaction) => (
        <span className={`font-semibold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-500'}`}>
          {formatEuro(tx.amount)}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('transactions.status'),
      align: 'center' as const,
      render: (tx: Transaction) =>
        tx.verified ? (
          <Badge variant="success" dot>{t('transactions.statusValues.verified')}</Badge>
        ) : (
          <Badge variant="warning" dot>{t('transactions.statusValues.pending')}</Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right' as const,
      render: (tx: Transaction) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => openEditModal(tx)}
            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
            title={t('common.edit')}
          >
            ✏️
          </button>
          <button
            onClick={() => setShowDeleteConfirm(tx.id)}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
            title={t('common.delete')}
          >
            🗑️
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={`💰 ${t('transactions.title')} — ${activeOrg.name}`}
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => window.location.href = '/reconciliation'}>
              🏦 {t('transactions.bankStatements')}
            </Button>
            <Button onClick={openCreateModal}>+ {t('transactions.new')}</Button>
          </div>
        }
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">✕</button>
        </div>
      )}

      <FilterBar>
        <SearchFilter
          value={filter}
          onChange={setFilter}
          placeholder={t('transactions.searchPlaceholder')}
        />
        <SelectFilter
          value={periodFilter}
          onChange={setPeriodFilter}
          options={periodOptions.filter(o => o.value !== '')}
          placeholder={t('transactions.allYears')}
        />
      </FilterBar>

      <Card>
        <Table
          columns={columns}
          data={filteredTransactions}
          keyExtractor={(tx) => tx.id}
          emptyMessage={t('transactions.empty')}
        />
      </Card>

      <SummaryFooter count={filteredTransactions.length} totalAmount={totalAmount} />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingTransaction ? t('transactions.edit') : t('transactions.new')}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField label={t('transactions.form.date')} required>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </FormField>
                
                <FormField label={t('transactions.form.amount')} required>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder={t('transactions.form.amountPlaceholder')}
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                </FormField>
              </div>
              
              <FormField label={t('transactions.form.description')}>
                <Input
                  type="text"
                  placeholder={t('transactions.form.descriptionPlaceholder')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </FormField>
              
              <FormField label={t('transactions.form.counterparty')}>
                <Input
                  type="text"
                  placeholder={t('transactions.form.counterpartyPlaceholder')}
                  value={formData.counterparty}
                  onChange={(e) => setFormData({ ...formData, counterparty: e.target.value })}
                />
              </FormField>
              
              <div className="grid grid-cols-2 gap-4">
                <FormField label={t('transactions.form.category')}>
                  <Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    options={CATEGORY_OPTIONS}
                  />
                </FormField>
                
                <FormField label={t('transactions.form.accountCode')}>
                  <Select
                    value={formData.accountCode}
                    onChange={(e) => setFormData({ ...formData, accountCode: e.target.value })}
                    options={ACCOUNT_CODE_OPTIONS}
                  />
                </FormField>
              </div>
              
              <FormField label={t('transactions.form.vatRate')} helperText={t('transactions.form.vatHelper')}>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="24"
                  value={formData.vatRate}
                  onChange={(e) => setFormData({ ...formData, vatRate: e.target.value })}
                />
              </FormField>

              <FormActions>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowModal(false)}
                  disabled={formSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={formSubmitting}
                >
                  {formSubmitting ? t('transactions.form.saving') : (editingTransaction ? t('transactions.form.saveChanges') : t('transactions.form.create'))}
                </Button>
              </FormActions>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('transactions.delete.title')}</h2>
            <p className="text-gray-600 mb-6">
              {t('transactions.delete.message')}
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1"
              >
                {t('transactions.delete.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
